import { RequestFailureKind } from '@amzn/aac-chrome-internal-api';
import { logger } from 'src/commons/utils/logger';
import { AjaxRequestPublisher } from 'src/commons/utils/metrics';
import {
  NETWORK_ERROR_REGEX,
  REQUEST_ERROR_REGEX,
  USER_ABORTED_REQUEST_ERROR,
} from './constants';

/*
 * The counter suffix each classified failure gets, on top of the `:success` 0
 * every failure already publishes. Only the two kinds with a dashboard behind
 * them are counted; the rest are distinguishable in the logs (which carry the
 * kind and the status) without adding a series nothing reads.
 *
 * `timeout` is a NEW series. Before fetchWithTimeout classified failures, this
 * module's own timeout surfaced as a DOMException whose message matched
 * USER_ABORTED_REQUEST_ERROR, so it was counted as `:userAbortedRequest` --
 * a name that says the user gave up when in fact the client's own deadline
 * fired. Caller cancellations never reach here at all (fetchWithTimeout
 * reports nothing for them), so `:userAbortedRequest` was only ever timeouts.
 */
const KIND_METRIC_SUFFIX: Partial<Record<RequestFailureKind, string>> = {
  network: 'networkFailure',
  timeout: 'timeout',
};

type HandleErrorProps = {
  // `unknown`, not `Error`: fetch-request forwards whatever `fetch` rejected
  // with, so a non-Error value has to be handled rather than assumed away.
  error: unknown;
  loggerMessage: string;
  metricSlug: string;
  /*
   * fetchWithTimeout's classification. Absent when the failure came from a
   * caller that does not classify -- fetchRequest, or a page running an OLDER
   * global window.AACChrome.InternalAPI whose fetchWithTimeout predates
   * `kind` -- in which case the error-message regexes below still decide.
   */
  kind?: RequestFailureKind;
  // The HTTP status, when the failure came with a response.
  status?: number;
};

const logError = ({
  error,
  loggerMessage,
  metricSlug,
  kind,
  status,
}: HandleErrorProps) => {
  const context = {
    ...(kind !== undefined ? { kind } : {}),
    ...(status !== undefined ? { status } : {}),
  };
  const hasContext = Object.keys(context).length > 0;

  // Pass the Error itself so KatalLogger records its name, message and stack.
  // Stringifying a wrapper object here used to log the literal
  // "[object Object]" and discard all three. `kind` and `status` ride in the
  // THIRD argument -- KatalLogger's (message, Error, context) overload keeps
  // the error fields and merges the context into the same record, so neither
  // has to be given up for the other.
  if (error instanceof Error) {
    if (hasContext) {
      logger.error(loggerMessage, error, context);
    } else {
      logger.error(loggerMessage, error);
    }
  } else {
    // KatalLogger spreads a non-Error second argument into the record's
    // context. Wrapping the value in `new Error()` would invent a stack
    // pointing here.
    logger.error(loggerMessage, { error: String(error), ...context });
  }

  if (kind !== undefined) {
    const suffix = KIND_METRIC_SUFFIX[kind];
    if (suffix) {
      AjaxRequestPublisher.publishCounterMonitor(`${metricSlug}:${suffix}`, 1);
    }
    return;
  }

  const errorMessage = error instanceof Error ? error.message : String(error);
  if (NETWORK_ERROR_REGEX.test(errorMessage)) {
    AjaxRequestPublisher.publishCounterMonitor(
      `${metricSlug}:networkFailure`,
      1,
    );
  } else if (REQUEST_ERROR_REGEX.test(errorMessage)) {
    AjaxRequestPublisher.publishCounterMonitor(
      `${metricSlug}:requestAborted`,
      1,
    );
  } else if (USER_ABORTED_REQUEST_ERROR.test(errorMessage)) {
    AjaxRequestPublisher.publishCounterMonitor(
      `${metricSlug}:userAbortedRequest`,
      1,
    );
  }
};

export default logError;
